import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { type RootState, useTypedSelector } from "@/store/store";
import { useProjectLaunchStatus } from "@/utils/devServerUtils";
import { track } from "@/utils/events/tracker";
import GitProviderAccessModal from "../GitProviderAccessModal";
import { DeletedModal } from "../ProjectList/DeletedModal";
import ServerDownModal from "../ServerDownModal";
import UnauthorizedModal from "../UnauthorizedModal";
import ProjectLaunchProgress from "./ProjectLaunchProgress";

type Props = {};

export const useCanvasOverlayStates = () => {
  const projectLaunchStatus = useProjectLaunchStatus();

  const unauthorized = useSelector((state: any) => state.codebase.unauthorized);
  const deleted = useSelector((state: any) => state.codebase.deleted);
  const serverDown = useSelector((state: any) => state.codebase.serverDown);
  const githubAccess = useSelector((state: any) => state.codebase.githubAccess);
  const gitlabAccess = useTypedSelector((state) => state.codebase.gitlabAccess);
  const bitbucketAccess = useTypedSelector(
    (state) => state.codebase.bitbucketAccess,
  );

  const projectLoadingProgress = useSelector(
    (state: any) => state.supabaseStore.projectLoadingProgress,
  );

  const devServerStatus = useTypedSelector(
    (state) => state.codebase.devServerStatus,
  );

  const devServerResponsive =
    projectLoadingProgress?.dev_server_responding === true;

  const projectLaunchRunning = useCodebaseStore(
    (state) => state.projectLaunchRunning,
  );

  const containerPaused = Boolean(
    devServerStatus &&
      (devServerStatus?.containerStatus?.found || !projectLaunchRunning) &&
      !devServerStatus?.containerStatus?.running,
  );

  const terminalDisconnected = Boolean(
    devServerStatus &&
      !projectLaunchRunning &&
      devServerStatus?.containerStatus?.found &&
      devServerStatus?.containerStatus?.running &&
      !devServerStatus?.devServerConsoleConnected,
  );

  const devServerNotRunning = Boolean(
    devServerStatus &&
      !projectLaunchRunning &&
      devServerStatus?.containerStatus?.found &&
      devServerStatus?.containerStatus?.running &&
      !devServerStatus?.isServerRunning,
  );

  const [devServerNotRunningCount, setDevServerNotRunningCount] = useState(0);
  const [certainDevServerNotRunning, setCertainDevServerNotRunning] =
    useState(false);

  // Whether to show the progress modal at all
  let showGithubModal = false;
  let showGitlabModal = false;
  let showBitbucketModal = false;
  let showUnauthorizedModal = false;
  let showProgressModal = false;
  let showDeletedModal = false;
  let showServerDownModal = false;
  if (unauthorized && !serverDown) {
    showUnauthorizedModal = true;
  } else if (deleted) {
    showDeletedModal = true;
  } else if (githubAccess === false) {
    showGithubModal = true;
  } else if (gitlabAccess === false) {
    showGitlabModal = true;
  } else if (bitbucketAccess === false) {
    showBitbucketModal = true;
  } else if (serverDown) {
    showServerDownModal = true;
  } else if (projectLaunchStatus !== "succeeded") {
    showProgressModal = true;
  } else if (
    containerPaused ||
    certainDevServerNotRunning ||
    !devServerResponsive
    // terminalDisconnected
  ) {
    showProgressModal = true;
  }

  useEffect(() => {
    if (devServerNotRunning) {
      setDevServerNotRunningCount((prev) => prev + 1);
      if (devServerNotRunningCount >= 2) {
        setCertainDevServerNotRunning(true);
      }
    } else {
      setDevServerNotRunningCount(0);
      setCertainDevServerNotRunning(false);
    }
  }, [devServerStatus, devServerNotRunning]);

  const anyModalVisible = useMemo(() => {
    return (
      showProgressModal ||
      showDeletedModal ||
      showUnauthorizedModal ||
      showGithubModal ||
      showGitlabModal ||
      showServerDownModal
    );
  }, [
    showProgressModal,
    showDeletedModal,
    showUnauthorizedModal,
    showGithubModal,
    showGitlabModal,
    showServerDownModal,
  ]);

  return {
    showProgressModal,
    showDeletedModal,
    showUnauthorizedModal,
    showGithubModal,
    showGitlabModal,
    showBitbucketModal,
    showServerDownModal,
    containerPaused,
    certainDevServerNotRunning,
    terminalDisconnected,
    anyModalVisible,
  };
};

const CanvasLoadingOverlay: React.FC<Props> = React.memo(() => {
  const {
    showProgressModal,
    showDeletedModal,
    showUnauthorizedModal,
    showGithubModal,
    showGitlabModal,
    showBitbucketModal,
    showServerDownModal,
    containerPaused,
    certainDevServerNotRunning,
    terminalDisconnected,
    anyModalVisible,
  } = useCanvasOverlayStates();

  /**
   * Track in posthog
   */
  useEffect(() => {
    if (terminalDisconnected) {
      track("TERMINAL_DISCONNECTED");
    }
  }, [terminalDisconnected]);

  useEffect(() => {
    if (showDeletedModal) {
      track("CANVAS_OVERLAY", {
        type: "CANVAS_DELETED",
      });
    }
  }, [showDeletedModal]);

  useEffect(() => {
    if (showUnauthorizedModal) {
      track("CANVAS_OVERLAY", {
        type: "UNAUTHORIZED",
      });
    }
  }, [showUnauthorizedModal]);

  useEffect(() => {
    if (showGithubModal) {
      track("CANVAS_OVERLAY", {
        type: "GITHUB_ACCESS",
      });
    }
  }, [showGithubModal]);

  useEffect(() => {
    if (showServerDownModal) {
      track("CANVAS_OVERLAY", {
        type: "SERVER_DOWN",
      });
    }
  }, [showServerDownModal]);

  return (
    <div
      className="absolute top-0 left-0 right-0 bottom-0"
      style={{ pointerEvents: anyModalVisible ? "auto" : "none" }}
    >
      {showServerDownModal && <ServerDownModal />}
      {showUnauthorizedModal && <UnauthorizedModal />}
      {showGithubModal && <GitProviderAccessModal provider="Github" />}
      {showGitlabModal && <GitProviderAccessModal provider="GitLab" />}
      {showBitbucketModal && <GitProviderAccessModal provider="Bitbucket" />}
      <ProjectLaunchProgress
        hidden={!showProgressModal}
        containerPaused={containerPaused}
        devServerNotRunning={certainDevServerNotRunning}
        terminalDisconnected={false}
      />
      {showDeletedModal && <DeletedModal />}
    </div>
  );
});

export default CanvasLoadingOverlay;
